import { getUrlStringParamsFromObject } from "./browser.utils";
import isNil from "lodash/isNil";

export const getCookie = cookieName =>
  document.cookie.replace(
    new RegExp(`(?:(?:^|.*;\\s*)${cookieName}\\s*\\=\\s*([^;]*).*$)|^.*$`),
    "$1"
  );

/**
 * Convert an object to URL params
 * @param {object} params - An object with the keys and the values of the URL params.
 *
 * @returns {string} An URL params.
 *                   Example: my_first_param=my_value&another_param=another_value
 */
export const stringifyParams = params => {
  if (!params) return "";
  return Object.entries(params)
    .filter(({ 1: value }) => !isNil(value) && !Array.isArray(value))
    .map(([key, value]) => {
      const encodeKey = encodeURIComponent(key).replace(/!/g, "%21");
      return value === null
        ? encodeKey
        : `${encodeKey}=${encodeURIComponent(value)}`;
    })
    .sort()
    .join("&");
};

const isSuccessful = statusCode => statusCode >= 200 && statusCode < 400;
const csrfToken = getCookie("csrftoken");

const getMethod = (method = "get") => {
  const validMethods = ["get", "post", "patch", "delete", "put"];
  if (validMethods.includes(method.toLowerCase())) {
    return method.toUpperCase();
  }

  throw new Error(`Wrong method used. ${method} is not a valid method.`);
};

const getHeaders = (method, originalHeaders = {}) => {
  let headers = {
    ...originalHeaders,
  };
  headers["Content-Type"] = "application/json";

  // if (method.toLowerCase() !== "get") {
  //   headers["X-CSRFToken"] = csrfToken;
  // }

  return headers;
};

const getBody = (body, headers) => {
  const contentType = headers["Content-Type"]
    ? headers["Content-Type"].toLowerCase()
    : null;
  if (contentType === "application/json" && body != null) {
    return JSON.stringify(body);
  }
  return body;
};

const makeRequest = async (url, options = {}) => {
  const {
    params: rawParams,
    method: rawMethod,
    headers: rawHeaders,
    body: rawBody,
    cache: rawCache,
    signal,
  } = options;

  let requestUrl = url;
  const params = getUrlStringParamsFromObject(rawParams);
  const method = getMethod(rawMethod);
  const headers = getHeaders(method, rawHeaders);
  const body = getBody(rawBody, headers);
  const cache = rawCache || "default";

  if (params && params !== "") {
    requestUrl = `${requestUrl}?${params}`;
  }
  try {
    return await fetch(requestUrl, {
      body,
      method,
      cache,
      headers,
      signal,
      credentials: "same-origin",
    });
  } catch (error) {
    return {
      code: undefined,
      data: undefined,
      isSuccessful: false,
    };
  }
};

const handleResponse = async response => {
  let responseText = "";
  let parsedResponse = "";
  const code = response.status;
  if (response.body != null) {
    responseText = await response.text();
  }

  try {
    parsedResponse = JSON.parse(responseText);
  } catch (ex) {
    parsedResponse = responseText;
  }

  return {
    code,
    data: parsedResponse,
    isSuccessful: isSuccessful(code),
  };
};

export { makeRequest, handleResponse, isSuccessful };
